import { useState, useRef, useEffect } from 'react';
import { CalendarPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';

function parseTimeSlot(date, timeSlot) {
  // Parse time_slot like "9:00 AM - 10:00 AM" or "9:00 AM" into start/end Date objects
  if (!timeSlot) {
    // Default: 1 hour event at noon
    const start = new Date(date + 'T12:00:00');
    const end = new Date(date + 'T13:00:00');
    return { start, end };
  }

  const parts = timeSlot.split(/\s*[-–]\s*/);
  const parseTime = (str, dateStr) => {
    const match = str.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
    if (!match) return null;
    let hours = parseInt(match[1]);
    const mins = parseInt(match[2]);
    const period = (match[3] || '').toUpperCase();
    if (period === 'PM' && hours < 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return new Date(`${dateStr}T${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:00`);
  };

  const start = parseTime(parts[0], date);
  const end = parts[1] ? parseTime(parts[1], date) : (start ? new Date(start.getTime() + 3600000) : null);

  if (!start) {
    return { start: new Date(date + 'T12:00:00'), end: new Date(date + 'T13:00:00') };
  }

  return { start, end: end || new Date(start.getTime() + 3600000) };
}

function toGoogleFormat(dt) {
  return dt.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

function toICSFormat(dt) {
  return dt.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
}

function generateGoogleUrl(title, date, timeSlot, description, location) {
  const { start, end } = parseTimeSlot(date, timeSlot);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${toGoogleFormat(start)}/${toGoogleFormat(end)}`,
    details: description,
    location: location || '',
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function generateOutlookUrl(title, date, timeSlot, description, location) {
  const { start, end } = parseTimeSlot(date, timeSlot);
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: title,
    startdt: start.toISOString(),
    enddt: end.toISOString(),
    body: description,
    location: location || '',
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

function generateICS(title, date, timeSlot, description, location) {
  const { start, end } = parseTimeSlot(date, timeSlot);
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//SplashAble//EN',
    'BEGIN:VEVENT',
    `DTSTART:${toICSFormat(start)}`,
    `DTEND:${toICSFormat(end)}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${description.replace(/\n/g, '\\n')}`,
    `LOCATION:${location || ''}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
  return ics;
}

function downloadICS(title, date, timeSlot, description, location) {
  const ics = generateICS(title, date, timeSlot, description, location);
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title.replace(/[^a-zA-Z0-9]/g, '_')}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function AddToCalendarButton({ title, date, timeSlot, description = '', location = '', size = 'icon' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost"
        size={size}
        className="h-9 w-9 text-slate-400 hover:text-[#0E7490] hover:bg-[#ECFEFF] rounded-full"
        onClick={() => setOpen(!open)}
        title="Add to Calendar"
        data-testid="add-to-calendar-btn"
      >
        <CalendarPlus className="w-4 h-4" />
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-xl shadow-xl border border-slate-200 py-1 w-48 animate-fadeIn" data-testid="calendar-dropdown">
          <button
            className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 flex items-center gap-2"
            onClick={() => { window.open(generateGoogleUrl(title, date, timeSlot, description, location), '_blank'); setOpen(false); }}
            data-testid="cal-google"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Google Calendar
          </button>
          <button
            className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 flex items-center gap-2"
            onClick={() => { window.open(generateOutlookUrl(title, date, timeSlot, description, location), '_blank'); setOpen(false); }}
            data-testid="cal-outlook"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.583a.795.795 0 01-.583.238h-8.322V6.566h8.322c.23 0 .424.08.583.238.159.159.238.353.238.583zM14.857 6.566v12.12H7.99l-6.82-1.202V6.516L7.99 5.314h6.866z"/><path fill="#0078D4" d="M7.5 10.5c-.828 0-1.5 1.007-1.5 2.25S6.672 15 7.5 15s1.5-1.007 1.5-2.25S8.328 10.5 7.5 10.5z" opacity=".5"/></svg>
            Outlook
          </button>
          <button
            className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 flex items-center gap-2"
            onClick={() => { downloadICS(title, date, timeSlot, description, location); setOpen(false); }}
            data-testid="cal-apple"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#333" d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83z"/><path fill="#333" d="M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
            Apple Calendar
          </button>
        </div>
      )}
    </div>
  );
}

// Helper to generate calendar link HTML for emails
export function generateCalendarLinksHTML(title, date, timeSlot, description, location) {
  const googleUrl = generateGoogleUrl(title, date, timeSlot, description, location);
  const outlookUrl = generateOutlookUrl(title, date, timeSlot, description, location);
  return `
    <div style="margin-top:16px;padding:12px;background:#f0fdfa;border-radius:12px;text-align:center;">
      <p style="margin:0 0 8px;font-size:13px;color:#64748b;font-weight:600;">Add to Calendar</p>
      <a href="${googleUrl}" target="_blank" style="display:inline-block;padding:8px 16px;background:#4285F4;color:white;border-radius:20px;text-decoration:none;font-size:12px;font-weight:bold;margin:0 4px;">Google</a>
      <a href="${outlookUrl}" target="_blank" style="display:inline-block;padding:8px 16px;background:#0078D4;color:white;border-radius:20px;text-decoration:none;font-size:12px;font-weight:bold;margin:0 4px;">Outlook</a>
    </div>
  `;
}
